import { LEVEL_COUNT, type Unit } from "@sins2-atlas/game-data";
import { Fragment, type ReactNode } from "react";
import type { PointsValue } from "../lib/ability-points";
import { COMPARE_LIMIT, compareOverflow, malformedCompareTokens } from "../lib/compare";
import { FACTION_TOKENS, type FactionSelection, sins1FactionNote } from "../lib/faction-selection";
import {
  type FrameSearch,
  parseFrame,
  type StatFrame,
  unhonouredLevel,
  unhonouredLoadout,
  unhonouredPoints,
  unknownCorruptionToken,
  unknownCorruptionTokensInSlots,
  unknownLevelToken,
  unknownLevelTokensInSlots,
  unknownLoadoutTokens,
  unknownLoadoutTokensInSlots,
  unknownPointsToken,
  unknownPointsTokensInSlots,
  unknownTechTokens,
  unknownTechTokensInSlots,
} from "../lib/frame";
import { unhonouredCorruption } from "../lib/frame-labels";
import type { LevelValue } from "../lib/level";

// One voice for "the URL asked for something this page could not honour" (ADR 0002
// amendment 2026-07-30). Every surface that reads URL state renders its unhonoured tokens
// through these, so a shared link is never silently reinterpreted and the explanation reads
// the same wherever it appears. The *decisions* — which tokens count as unhonoured — live in
// the lib modules and are pinned there; this file only says them out loud.

/** The standard notice paragraph: quiet, above the content it explains. */
export function UrlNotice({ children }: { children: ReactNode }) {
  return <p className="text-[13px] text-muted-2">{children}</p>;
}

/** A token quoted verbatim from the URL, set apart from the prose around it. */
export function UrlToken({ children }: { children: ReactNode }) {
  return <span className="font-mono text-ink-3">{children}</span>;
}

/** What `?tech=` asked for that this page could not honour (Issue 24, ADR 0009): ids the
 * dataset knows nothing about — including the word `all` inside a list, which names the
 * whole extent only as the entire value (ADR 0011). Nothing is silently resolved: the tokens
 * stay on every link out. There is no fully-teched-versus-set conflict left to report, one
 * key being unable to contradict itself. */
export function TechNotice({ search }: { search: FrameSearch }) {
  return <UnknownTechNotice tokens={unknownTechTokens(search.tech)} />;
}

/** The same on /compare, where `?tech=` carries one slot per column (ADR 0011): every slot's
 * unrecognised tokens, gathered. The single-slot reader cannot serve it — a legal `all;bogus`
 * is one string, and reading it whole quoted the reader's own good slot back at them. */
export function TechSlotsNotice({ value }: { value: string | undefined }) {
  return <UnknownTechNotice tokens={unknownTechTokensInSlots(value)} />;
}

function UnknownTechNotice({ tokens }: { tokens: readonly string[] }) {
  if (tokens.length === 0) return null;
  return (
    <UrlNotice>
      Not a research subject or tier in this dataset: <UrlToken>{tokens.join(", ")}</UrlToken>.
      Nothing was applied for {tokens.length > 1 ? "them" : "it"}, and{" "}
      {tokens.length > 1 ? "they stay" : "it stays"} on every link from here.
    </UrlNotice>
  );
}

/** What `?items=` asked for that this page could not equip (ADR 0012, Issue 44): ids the
 * dataset ships no item for, and ids it does ship but this hull's shop does not list — a
 * Loadout belongs to one hull, so an item another hull buys is not an item this one carries.
 * Neither is silently resolved and neither folds. Unlike `?tech=`, the tokens do NOT ride
 * links out: `?items=` rides no unit-level link at all. */
export function LoadoutNotice({ unit, search }: { unit?: Unit; search: FrameSearch }) {
  const unknown = unknownLoadoutTokens(search.items);
  const unbuyable = unit === undefined ? [] : unhonouredLoadout(unit, parseFrame(search));
  if (unknown.length === 0 && unbuyable.length === 0) return null;
  return (
    <UrlNotice>
      {unknown.length > 0 && (
        <>
          Not a ship item in this dataset: <UrlToken>{unknown.join(", ")}</UrlToken>. Nothing was
          equipped for {unknown.length > 1 ? "them" : "it"}.{" "}
        </>
      )}
      {unbuyable.length > 0 && <UnbuyableSentence ids={unbuyable} whose="this hull's" where="" />}
    </UrlNotice>
  );
}

/** The one sentence for an item a hull's shop does not list, on the entity page ("this hull's")
 * and per column on the comparison (the hull's name, "in its column"). */
function UnbuyableSentence({
  ids,
  whose,
  where,
}: {
  ids: readonly string[];
  whose: string;
  where: string;
}) {
  return (
    <>
      <UrlToken>{ids.join(", ")}</UrlToken> {ids.length > 1 ? "are items" : "is an item"} {whose}{" "}
      shop does not list, so {ids.length > 1 ? "they are" : "it is"} not equipped{where}.
    </>
  );
}

/** The one sentence for a Level on a hull with no ladder, on the entity page and per column
 * on the comparison. */
function NoLevelsSentence({ unit, level, where }: { unit: Unit; level: number; where: string }) {
  return (
    <>
      {unit.name} gains no levels — only capital ships, command ships and titans do — so level{" "}
      {level} changes nothing {where}.
    </>
  );
}

/** The one sentence for an allocation a hull could not have bought, on the entity page and
 * per column on the comparison. */
function UnreachableAllocationSentence({
  unit,
  allocation,
  where,
}: {
  unit: Unit;
  allocation: readonly number[];
  where: string;
}) {
  return (
    <>
      <UrlToken>{allocation.join(",")}</UrlToken> is not an allocation the {unit.name} could have
      bought{unit.leveled ? " at the level shown" : ": it has no Level, so no ability points"}.
      Nothing is spent{where}.
    </>
  );
}

/** What a per-hull lever asked of a comparison column whose hull cannot honour it (Issue 52,
 * Issue 63): a Level on a hull with no ladder, an item its shop does not list, an allocation
 * it could not have bought. Each is honoured on the columns that CAN carry it — the slots are
 * independent — so this names the hull rather than the column number, and says it once
 * however many columns repeat it. The sentences are LevelNotice's, LoadoutNotice's and
 * AbilityPointsNotice's own, so the explanation reads the same wherever it appears (moved here
 * from routes/compare.tsx, review 2026-09-08). */
export function UnhonouredLeverNotice({
  columns,
}: {
  columns: readonly { unit: Unit; frame: StatFrame }[];
}) {
  const said = new Map<string, ReactNode>();
  for (const { unit, frame } of columns) {
    const level = unhonouredLevel(unit, frame);
    if (level !== undefined) {
      said.set(
        `level|${unit.id}|${level}`,
        <NoLevelsSentence unit={unit} level={level} where="in its column" />,
      );
    }
    const unbuyable = unhonouredLoadout(unit, frame);
    if (unbuyable.length > 0) {
      said.set(
        `items|${unit.id}|${unbuyable.join(",")}`,
        <UnbuyableSentence ids={unbuyable} whose={`the ${unit.name}'s`} where=" in its column" />,
      );
    }
    const unreachable = unhonouredPoints(unit, frame);
    if (unreachable !== undefined) {
      said.set(
        `points|${unit.id}|${unreachable.join(",")}`,
        <UnreachableAllocationSentence
          unit={unit}
          allocation={unreachable}
          where=" in its column"
        />,
      );
    }
  }
  if (said.size === 0) return null;
  return (
    <UrlNotice>
      {[...said].map(([key, sentence], index) => (
        <Fragment key={key}>
          {index > 0 && " "}
          {sentence}
        </Fragment>
      ))}
    </UrlNotice>
  );
}

/** What a `?items=` link had to complete before it named a Loadout the game can produce
 * (issue 45's cold-parse gap, found reviewing the Cloak round 2026-09-07): the rungs beneath
 * an item, the research that buys it, whatever the game forbids beside it. The link is
 * honoured rather than refused, so unlike the notices around it this reports a change the
 * page MADE — in the same words the equivalent tick would have used, because it is the same
 * door. Silence here means the link already named a Loadout a player could field. */
export function LoadoutSettledNotice({ notes }: { notes: readonly string[] }) {
  const said = [...new Set(notes)];
  if (said.length === 0) return null;
  return <UrlNotice>{said.join(" ")}</UrlNotice>;
}

/** The same on /compare, where `?items=` carries one slot per column (ADR 0011 + ADR 0012):
 * every slot's unrecognised ids, gathered. Only the dataset's half — whether a hull's own shop
 * lists an item is a per-COLUMN question, and /compare answers it per column. */
export function LoadoutSlotsNotice({ value }: { value: string | undefined }) {
  const tokens = unknownLoadoutTokensInSlots(value);
  if (tokens.length === 0) return null;
  return (
    <UrlNotice>
      Not a ship item in this dataset: <UrlToken>{tokens.join(", ")}</UrlToken>. Nothing was
      equipped for {tokens.length > 1 ? "them" : "it"}.
    </UrlNotice>
  );
}

/** The same on /compare for `?level=`: every slot naming a rung the game's ladder does not
 * have. Each such column reads at level 1, exactly as a single-frame page's does. */
export function LevelSlotsNotice({ value }: { value: LevelValue | undefined }) {
  const tokens = unknownLevelTokensInSlots(value);
  if (tokens.length === 0) return null;
  return (
    <UrlNotice>
      <UrlToken>{tokens.join(", ")}</UrlToken>{" "}
      {tokens.length > 1 ? "are not levels" : "is not a level"} a hull can reach; the game's ladder
      runs 1 to {LEVEL_COUNT}, so {tokens.length > 1 ? "those columns are" : "that column is"} at
      level 1.
    </UrlNotice>
  );
}

/** What `?level=` asked for that this page could not honour (ADR 0012, Issue 49): a token that
 * is no rung of the game's 1-to-10 ladder, and a real level on a hull that has none — a
 * frigate, a structure and a strikecraft never gain one, so nothing on their page moves.
 * Neither is silently resolved; both read at level 1. Like `?items=`, the tokens do not ride
 * links out: `?level=` rides no unit-level link at all. */
export function LevelNotice({ unit, search }: { unit?: Unit; search: FrameSearch }) {
  const unknown = unknownLevelToken(search.level);
  const unreachable = unit === undefined ? undefined : unhonouredLevel(unit, parseFrame(search));
  if (unknown === undefined && unreachable === undefined) return null;
  return (
    <UrlNotice>
      {unknown !== undefined && (
        <>
          <UrlToken>{unknown}</UrlToken> is not a level this hull can reach; the game's ladder runs
          1 to {LEVEL_COUNT}, so these are level 1 stats.{" "}
        </>
      )}
      {unreachable !== undefined && unit !== undefined && (
        <NoLevelsSentence unit={unit} level={unreachable} where="on this page" />
      )}
    </UrlNotice>
  );
}

/** What `?points=` asked for that this page could not honour (ADR 0015, Issue 60): a value
 * that is no list of whole numbers, and a list this hull could not have bought — more
 * abilities than it has, an Ability Level above what its Level permits, or more Ability
 * Points than the Level gives. Neither is silently resolved and neither folds: the hull reads
 * with nothing spent, which is what a freshly built one reads. Like `?items=` and `?level=`,
 * the value does not ride links out — `?points=` rides no unit-level link at all. */
export function AbilityPointsNotice({ unit, search }: { unit?: Unit; search: FrameSearch }) {
  const unknown = unknownPointsToken(search.points);
  const unreachable = unit === undefined ? undefined : unhonouredPoints(unit, parseFrame(search));
  if (unknown === undefined && unreachable === undefined) return null;
  return (
    <UrlNotice>
      {unknown !== undefined && (
        <>
          <UrlToken>{unknown}</UrlToken> is not an ability point allocation: the key lists one whole
          number per ability, in the game's order. Nothing is spent.{" "}
        </>
      )}
      {unreachable !== undefined && unit !== undefined && (
        <UnreachableAllocationSentence unit={unit} allocation={unreachable} where="" />
      )}
    </UrlNotice>
  );
}

/** The same on /compare, where `?points=` carries one slot per column (ADR 0011 + ADR 0015):
 * every slot that is no allocation the grammar admits, gathered. Only the grammar's half —
 * whether a hull could have BOUGHT what its slot names is a per-COLUMN question, and
 * UnhonouredLeverNotice answers it per column, the way it does for `?items=`. */
export function AbilityPointsSlotsNotice({ value }: { value: PointsValue | undefined }) {
  const tokens = unknownPointsTokensInSlots(value);
  if (tokens.length === 0) return null;
  return (
    <UrlNotice>
      <UrlToken>{tokens.join(", ")}</UrlToken>{" "}
      {tokens.length > 1
        ? "are not ability point allocations"
        : "is not an ability point allocation"}
      : the key lists one whole number per ability, in the game's order, one slot per column.
      Nothing is spent in {tokens.length > 1 ? "those columns" : "that column"}.
    </UrlNotice>
  );
}

/** The Corruption Tier fell back to uncorrupted because the URL named something the game
 * does not (ADR 0007). Never says "tier" (CONTEXT.md) — it names the game's own adjectives. */
export function CorruptionNotice({ value }: { value: string | undefined }) {
  const token = unknownCorruptionToken(value);
  return <UnknownCorruptionNotice tokens={token === undefined ? [] : [token]} />;
}

/** The same on /compare, where `?corruption=` carries one slot per column (ADR 0011). Each
 * unrecognised slot falls back to uncorrupted on its own column, exactly as a single-frame
 * page's does. */
export function CorruptionSlotsNotice({ value }: { value: string | undefined }) {
  return <UnknownCorruptionNotice tokens={unknownCorruptionTokensInSlots(value)} />;
}

function UnknownCorruptionNotice({ tokens }: { tokens: readonly string[] }) {
  if (tokens.length === 0) return null;
  return (
    <UrlNotice>
      <UrlToken>{tokens.join(", ")}</UrlToken>{" "}
      {tokens.length > 1
        ? "are not Corruptions the game names, so those columns are uncorrupted."
        : "is not a Corruption the game names, so these are uncorrupted stats."}{" "}
      The game names mild, moderate and extreme.
    </UrlNotice>
  );
}

/** The URL names a real Corruption but the opened unit has no pool (strikecraft, Envoys),
 * so nothing on the page moves under it. The token is honoured — it still follows every
 * link out — but the page says why it changes nothing here. */
export function CorruptionPoolNotice({ unit, frame }: { unit: Unit; frame: StatFrame }) {
  const tier = unhonouredCorruption(unit, frame);
  if (tier === undefined) return null;
  return (
    <UrlNotice>
      {unit.name} has no corruption pool, so {tier} changes nothing on this page. The Corruption
      still follows every link from here.
    </UrlNotice>
  );
}

/** What a compare param lost: tokens that could not be a unit id, and ids past the cap.
 * `overflow` is opt-in because /compare states the cap in its own words already. */
export function CompareParamNotice({
  value,
  requested,
  reportOverflow = false,
}: {
  value: string | undefined;
  /** Whatever the param asked for, uncapped — ids on the roster and entity pages, (unit,
   * frame) pairs on /compare (ADR 0011). Only its length is read. */
  requested: readonly unknown[];
  reportOverflow?: boolean;
}) {
  const malformed = malformedCompareTokens(value);
  const overflow = reportOverflow ? compareOverflow(requested) : 0;
  if (malformed.length === 0 && overflow === 0) return null;
  return (
    <UrlNotice>
      {malformed.length > 0 && (
        <>
          Not a unit id: <UrlToken>{malformed.join(", ")}</UrlToken> — dropped from the comparison
          set.{" "}
        </>
      )}
      {overflow > 0 && (
        <>
          This link lists {requested.length} units; the set keeps the first {COMPARE_LIMIT}.
        </>
      )}
    </UrlNotice>
  );
}

/** What `?faction=` named that is no Faction (ADR 0002 amendment, ADR 0010). The tokens are
 * never swallowed and never widen the selection back to All Factions, so the page must say
 * which ones it could not honour — otherwise an empty view is nobody's mistake. */
export function FactionNotice({ selection }: { selection: FactionSelection }) {
  const tokens = selection.unknown;
  if (tokens.length === 0) return null;
  // Loyalist/Rebel are the likeliest wrong guess — Sins I's names, and still the ids in
  // Sins II's own data files — so they get named as retired rather than as nonsense.
  const sins1 = sins1FactionNote(tokens);
  return (
    <UrlNotice>
      Unrecognized Faction{tokens.length > 1 ? "s" : ""}: <UrlToken>{tokens.join(", ")}</UrlToken> —
      no unit matches. Sins II Factions are {FACTION_TOKENS.map((entry) => entry.label).join(", ")}.
      {sins1 !== undefined && <> {sins1}</>}
    </UrlNotice>
  );
}
